import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";

const SITE_URL = "https://adrise-agency.com";

const CONTENT = {
    en: {
        title: "Terms of Service",
        subtitle: "Commercial terms for Adrise services and platform access.",
        seoTitle: "Adrise Terms of Service — Agency Account Platform Agreement",
        seoDescription:
            "Read Adrise Terms of Service covering platform eligibility, volumetric top-up fees, crypto funding terms, account usage, and legal limitations.",
        sections: [
            {
                h: "1) Scope of services",
                p: "Adrise provides managed access to agency advertising account infrastructure and credit-line-enabled operational services for approved clients. There is no monthly subscription.",
            },
            {
                h: "2) Eligibility and account approval",
                p: "Service is available only to users and entities that pass internal risk, compliance, and operational review. Adrise may accept, decline, limit, or revoke access at its sole discretion.",
            },
            {
                h: "3) Fees and billing",
                p: "Adrise charges no monthly subscription. A volumetric top-up fee, based on vertical class and monthly volume, is applied to wallet top-ups and settled in supported stablecoins. A platform-specific minimum first top-up is required to request an ad account.",
            },
            {
                h: "4) Wallet funding and settlement",
                p: "Users top up balances via unique deposit addresses generated per order. Funds are credited after blockchain confirmation thresholds and internal reconciliation.",
            },
            {
                h: "5) Permitted use and prohibited conduct",
                p: "You are responsible for campaign compliance, creatives, offers, and landing pages. Fraud, malware, chargeback abuse, sanctions violations, and illegal promotion are strictly prohibited.",
            },
            {
                h: "6) Service changes and suspension",
                p: "Adrise may modify fees, limits, available platforms, or technical workflows with prior notice where commercially reasonable. Immediate suspension may apply for abuse or legal exposure.",
            },
            {
                h: "7) Liability limitations",
                p: "To the maximum extent permitted by law, Adrise is not liable for indirect, special, or consequential losses, including campaign performance outcomes or third-party platform enforcement actions.",
            },
            {
                h: "8) Contact",
                p: "For legal and commercial inquiries, contact support via email or official Telegram channel listed on the Contact page.",
            },
        ],
    },
    pt: {
        title: "Termos de Serviço",
        subtitle: "Condições comerciais para serviços e acesso à plataforma Adrise.",
        seoTitle: "Adrise Termos de Serviço — Contrato da Plataforma",
        seoDescription:
            "Leia os Termos de Serviço da Adrise sobre elegibilidade, assinatura, pagamentos em cripto, uso da conta e limitações legais.",
        sections: [
            {
                h: "1) Escopo dos serviços",
                p: "A Adrise fornece acesso gerenciado à infraestrutura de contas de anúncios de agência, ferramentas por assinatura e serviços operacionais com linha de crédito para clientes aprovados.",
            },
            {
                h: "2) Elegibilidade e aprovação",
                p: "O serviço está disponível apenas para usuários e empresas aprovados em revisão interna de risco, compliance e operação. A Adrise pode aceitar, recusar, limitar ou revogar acesso a seu critério.",
            },
            {
                h: "3) Assinatura e cobrança",
                p: "As assinaturas são cobradas em equivalente USD e pagas em stablecoins suportadas. Assinatura ativa é obrigatória para solicitar e manter acesso às contas de anúncios.",
            },
            {
                h: "4) Depósito em carteira e conciliação",
                p: "Os usuários recarregam saldo por endereços de depósito únicos gerados por pedido. O crédito ocorre após confirmações em blockchain e conciliação interna.",
            },
            {
                h: "5) Uso permitido e conduta proibida",
                p: "Você é responsável por conformidade de campanhas, criativos, ofertas e landing pages. Fraude, malware, abuso, sanções e promoções ilegais são proibidos.",
            },
            {
                h: "6) Alterações e suspensão",
                p: "A Adrise pode ajustar taxas, limites, plataformas e fluxos técnicos com aviso prévio quando razoável. Suspensão imediata pode ocorrer em casos de abuso ou risco legal.",
            },
            {
                h: "7) Limitação de responsabilidade",
                p: "No limite permitido por lei, a Adrise não responde por perdas indiretas, especiais ou consequenciais, incluindo performance de campanha ou ações de plataformas terceiras.",
            },
            {
                h: "8) Contato",
                p: "Para temas legais e comerciais, contate por e-mail ou pelo canal oficial no Telegram informado na página de contato.",
            },
        ],
    },
    es: {
        title: "Términos de Servicio",
        subtitle: "Condiciones comerciales para servicios y acceso a Adrise.",
        seoTitle: "Adrise Términos de Servicio — Acuerdo de Plataforma",
        seoDescription:
            "Consulta los Términos de Servicio de Adrise sobre elegibilidad, suscripciones, pagos cripto, uso de cuentas y límites legales.",
        sections: [
            {
                h: "1) Alcance del servicio",
                p: "Adrise ofrece acceso gestionado a infraestructura de cuentas publicitarias de agencia, herramientas por suscripción y servicios operativos con línea de crédito para clientes aprobados.",
            },
            {
                h: "2) Elegibilidad y aprobación",
                p: "El servicio está disponible solo para usuarios y empresas que aprueben revisión interna de riesgo, cumplimiento y operación. Adrise puede aceptar, rechazar, limitar o revocar acceso a su discreción.",
            },
            {
                h: "3) Suscripción y facturación",
                p: "Las suscripciones se cobran en equivalente USD y se pagan en stablecoins admitidas. Se requiere suscripción activa para solicitar y mantener acceso a cuentas.",
            },
            {
                h: "4) Fondos y conciliación",
                p: "Los usuarios recargan saldo mediante direcciones únicas por pedido. Los fondos se acreditan tras umbrales de confirmación en blockchain y conciliación interna.",
            },
            {
                h: "5) Uso permitido y conductas prohibidas",
                p: "Usted es responsable del cumplimiento de campañas, creatividades, ofertas y landing pages. Se prohíben fraude, malware, abuso, sanciones y promoción ilegal.",
            },
            {
                h: "6) Cambios y suspensión",
                p: "Adrise puede ajustar tarifas, límites, plataformas o flujos técnicos con aviso previo cuando sea razonable. Puede aplicarse suspensión inmediata por abuso o riesgo legal.",
            },
            {
                h: "7) Limitación de responsabilidad",
                p: "En la máxima medida permitida por ley, Adrise no será responsable por pérdidas indirectas, especiales o consecuenciales, incluyendo resultados de campañas o acciones de plataformas de terceros.",
            },
            {
                h: "8) Contacto",
                p: "Para asuntos legales y comerciales, contacte por email o por el canal oficial de Telegram mostrado en la página de contacto.",
            },
        ],
    },
    ru: {
        title: "Условия обслуживания",
        subtitle: "Коммерческие условия использования сервисов Adrise.",
        seoTitle: "Adrise Условия обслуживания — Соглашение платформы",
        seoDescription:
            "Ознакомьтесь с условиями Adrise: допуск, подписка, крипто-платежи, использование аккаунтов и юридические ограничения.",
        sections: [
            {
                h: "1) Объем услуг",
                p: "Adrise предоставляет управляемый доступ к инфраструктуре агентских рекламных аккаунтов, подписочным инструментам и операционным сервисам с кредитной линией для одобренных клиентов.",
            },
            {
                h: "2) Допуск и одобрение",
                p: "Сервис доступен только пользователям и компаниям, прошедшим внутреннюю проверку рисков, compliance и операционных критериев. Adrise вправе одобрить, отклонить, ограничить или отозвать доступ.",
            },
            {
                h: "3) Подписка и расчеты",
                p: "Подписка рассчитывается в эквиваленте USD и оплачивается поддерживаемыми стейблкоинами. Для запроса и поддержания рекламных аккаунтов требуется активная подписка.",
            },
            {
                h: "4) Пополнение и зачисление",
                p: "Пополнение выполняется на уникальные депозитные адреса для каждого заказа. Средства зачисляются после порога подтверждений в блокчейне и внутренней сверки.",
            },
            {
                h: "5) Разрешенное использование и запреты",
                p: "Вы несете ответственность за соответствие кампаний, креативов, офферов и лендингов. Мошенничество, вредоносный код, злоупотребления, санкционные нарушения и незаконная реклама запрещены.",
            },
            {
                h: "6) Изменения сервиса и блокировка",
                p: "Adrise может менять тарифы, лимиты, доступные платформы и технические процессы с предварительным уведомлением, когда это коммерчески обосновано. При злоупотреблении возможна немедленная блокировка.",
            },
            {
                h: "7) Ограничение ответственности",
                p: "В максимально допустимой законом степени Adrise не несет ответственности за косвенные, специальные и последующие убытки, включая результаты рекламных кампаний и действия сторонних платформ.",
            },
            {
                h: "8) Контакты",
                p: "По юридическим и коммерческим вопросам используйте email или официальный Telegram, указанный на странице контактов.",
            },
        ],
    },
    tr: {
        title: "Hizmet Şartları",
        subtitle: "Adrise hizmetleri ve platform erişimi için ticari şartlar.",
        seoTitle: "Adrise Hizmet Şartları — Platform Kullanım Sözleşmesi",
        seoDescription:
            "Adrise Hizmet Şartları: uygunluk, hacim bazlı yükleme komisyonu, kripto ödeme koşulları, hesap kullanımı ve yasal sınırlar.",
        sections: [
            {
                h: "1) Hizmet kapsamı",
                p: "Adrise, onaylı müşterilere ajans reklam hesabı altyapısına yönetimli erişim ve kredi hattı destekli operasyonel hizmetler sunar. Aylık abonelik yoktur.",
            },
            {
                h: "2) Uygunluk ve hesap onayı",
                p: "Hizmet yalnızca iç risk, uyum ve operasyon incelemesini geçen kullanıcı ve şirketlere açıktır. Adrise; kabul, ret, kısıtlama veya erişim iptali kararını tek taraflı verebilir.",
            },
            {
                h: "3) Ücretler ve faturalama",
                p: "Adrise aylık abonelik ücreti almaz. Dikey sınıfı ve aylık hacme göre belirlenen yükleme komisyonu, cüzdan yüklemelerine uygulanır ve desteklenen stablecoinlerle ödenir. Reklam hesabı talebi için platforma göre minimum ilk yükleme tutarı gereklidir.",
            },
            {
                h: "4) Cüzdan fonlama ve mutabakat",
                p: "Kullanıcı bakiyesi, siparişe özel üretilen depozito adreslerine transfer ile yüklenir. Bakiye; blockchain onay eşiği ve iç mutabakat sonrası sisteme yansır.",
            },
            {
                h: "5) İzin verilen kullanım ve yasaklı faaliyetler",
                p: "Kampanya uyumu, kreatifler, teklifler ve landing page içeriklerinden kullanıcı sorumludur. Dolandırıcılık, kötü amaçlı yazılım, suistimal, yaptırım ihlali ve yasa dışı tanıtım kesinlikle yasaktır.",
            },
            {
                h: "6) Hizmet değişiklikleri ve askıya alma",
                p: "Adrise, makul ölçüde ön bilgilendirme yaparak ücret, limit, platform kapsamı ve teknik akışlarda değişiklik yapabilir. Kötüye kullanım veya hukuki riskte anlık askıya alma uygulanabilir.",
            },
            {
                h: "7) Sorumluluğun sınırlandırılması",
                p: "Yasaların izin verdiği azami ölçüde Adrise; dolaylı, özel veya sonuçsal zararlardan, kampanya performans sonuçlarından veya üçüncü taraf platform işlemlerinden sorumlu değildir.",
            },
            {
                h: "8) İletişim",
                p: "Hukuki ve ticari konular için Contact sayfasında listelenen resmi e-posta ve Telegram kanallarını kullanın.",
            },
        ],
    },
};

export default function TermsPage() {
    const { language } = useLanguage();
    const c = CONTENT[language] || CONTENT.en;
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: c.title,
        url: `${SITE_URL}/terms`,
        inLanguage: language,
        isPartOf: { "@type": "WebSite", name: "Adrise", url: SITE_URL },
    };

    return (
        <main data-testid="terms-page" className="bg-background text-foreground min-h-screen">
            <SEOHead
                title={c.seoTitle}
                description={c.seoDescription}
                canonical={`${SITE_URL}/terms`}
                keywords={[
                    "adrise terms",
                    "agency account agreement",
                    "credit line service terms",
                    "crypto payment terms",
                ]}
                jsonLd={jsonLd}
                alternates={[
                    { hrefLang: "en", href: `${SITE_URL}/terms` },
                    { hrefLang: "pt", href: `${SITE_URL}/terms` },
                    { hrefLang: "es", href: `${SITE_URL}/terms` },
                    { hrefLang: "ru", href: `${SITE_URL}/terms` },
                    { hrefLang: "tr", href: `${SITE_URL}/terms` },
                    { hrefLang: "x-default", href: `${SITE_URL}/terms` },
                ]}
            />

            <Navbar />

            <section className="pt-32 md:pt-40 pb-16" data-testid="terms-hero-section">
                <div className="container-skala">
                    <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        {c.title}
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-3xl">
                        {c.subtitle}
                    </p>
                </div>
            </section>

            <section className="pb-20" data-testid="terms-content-section">
                <div className="container-skala max-w-4xl space-y-7">
                    {c.sections.map((s) => (
                        <article key={s.h} className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid={`terms-section-${s.h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <h2 className="text-base md:text-lg font-semibold tracking-tight">{s.h}</h2>
                            <p className="text-sm md:text-base text-muted-foreground mt-3 leading-relaxed">{s.p}</p>
                        </article>
                    ))}
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
